import { useState, useEffect } from 'react';

interface MobileDetection {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  deviceType: 'mobile' | 'tablet' | 'desktop';
  isIOS: boolean;
  isAndroid: boolean;
  isTouchDevice: boolean;
}

export function useMobileDetection(): MobileDetection {
  const [detection, setDetection] = useState<MobileDetection>(() => {
    const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const height = typeof window !== 'undefined' ? window.innerHeight : 768;
    
    return {
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024,
      screenWidth: width,
      screenHeight: height,
      orientation: width > height ? 'landscape' : 'portrait',
      deviceType: width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop',
      isIOS: typeof window !== 'undefined' ? /iPad|iPhone|iPod/.test(navigator.userAgent) : false,
      isAndroid: typeof window !== 'undefined' ? /Android/.test(navigator.userAgent) : false,
      isTouchDevice: typeof window !== 'undefined' ? 'ontouchstart' in window : false
    };
  });

  useEffect(() => {
    const updateDetection = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setDetection({
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024,
        screenWidth: width,
        screenHeight: height,
        orientation: width > height ? 'landscape' : 'portrait',
        deviceType: width < 768 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop',
        isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
        isAndroid: /Android/.test(navigator.userAgent),
        isTouchDevice: 'ontouchstart' in window
      });
    };

    window.addEventListener('resize', updateDetection);
    window.addEventListener('orientationchange', updateDetection);
    
    return () => {
      window.removeEventListener('resize', updateDetection);
      window.removeEventListener('orientationchange', updateDetection);
    };
  }, []);

  return detection;
}

// Hook específico para determinar si usar componentes móviles
export function useMobileView() {
  const { isMobile, isTablet, deviceType } = useMobileDetection();
  
  return {
    shouldUseMobileView: isMobile,
    shouldUseTabletView: isTablet,
    preferMobileComponents: isMobile || isTablet,
    deviceType
  };
}

// Hook para gestos táctiles
export function useTouchGestures() {
  const { isTouchDevice, isMobile } = useMobileDetection();
  
  return {
    supportsTouchGestures: isTouchDevice && isMobile,
    shouldEnableSwipeNavigation: isTouchDevice && isMobile,
    touchOptimized: isTouchDevice
  };
}